import { Box, MenuItem, TextField } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import type { JSX } from "react";

import {
  LEAD_SOURCE_LABELS,
  type SalesLeadSource,
  type SalesVertical,
} from "@xop/dashboard-shared";

import { SALES_VERTICALS, verticalLabel } from "../components/VerticalBadge.js";
import {
  LEAD_SORT_PRESETS,
  describeSort,
  sameSort,
  type LeadSort,
} from "./leadSort.js";

// The selectable verticals (primary critical-infra first, then the light tail,
// then unknown) — shared with the inline classification editor on the leads
// list so the filter and the editor offer the exact same options.
const VERTICALS = SALES_VERTICALS;

/**
 * Filter row above the Leads table.
 *
 * Single-row layout: search · source · country · vertical · sort. The
 * previous version had the vertical as a pill cluster on its own row plus an
 * "Evidence kind" pill cluster below it; the operator asked for a tighter,
 * one-row filter bar so the table sits higher on the page. Vertical
 * collapsed to a `<select>` so it fits next to the two text inputs.
 *
 * The **Sort** select is a set of quick-pick presets ("Most urgent" / "Newest
 * first" / "Highest confidence") that drive a SERVER-side ordering of the whole
 * dataset — and it stays in SYNC with the table's column headers (which sort
 * the whole DB too): if a header click produced a different ordering, the
 * trigger reads it back ("Sort: Country (A–Z)"). See `leadSort.ts`.
 *
 * Evidence-kind filtering is dropped entirely — operators were sorting
 * by heat / confidence / freshness instead, and the closed-world enum
 * (tender / news / job_posting / …) doesn't even map cleanly to the
 * v4 free-text evidence kinds the agent emits now.
 */
export function LeadFilters({
  q,
  vertical,
  country,
  source,
  sort,
  onChange,
}: {
  q: string;
  vertical: SalesVertical | undefined;
  country: string;
  source: SalesLeadSource | undefined;
  sort: LeadSort;
  onChange: (
    patch: Partial<{
      q: string;
      vertical: SalesVertical | undefined;
      country: string;
      source: SalesLeadSource | undefined;
      sort: LeadSort;
    }>,
  ) => void;
}): JSX.Element {
  // Which preset (if any) the active sort matches — drives the Select's value
  // so the dropdown highlights it; "" when the sort came from a column header.
  const activePreset = LEAD_SORT_PRESETS.find((p) => sameSort(p.sort, sort));
  return (
    <Box
      sx={{
        display: "grid",
        gap: 1,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t: Theme) => t.shadows[1],
        p: 1.5,
        gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr 1fr 1.2fr" },
      }}
    >
      <TextField
        type="search"
        value={q}
        placeholder="Search org name…"
        onChange={(e) => onChange({ q: e.target.value })}
      />
      {/* Which bot made it — lets the operator see "just the Harvester's leads"
          or "just the Lead Generator's". Both feed this one pipeline. */}
      <TextField
        select
        value={source ?? ""}
        onChange={(e) =>
          onChange({
            source: e.target.value
              ? (e.target.value as SalesLeadSource)
              : undefined,
          })
        }
        aria-label="Filter by which agent found the lead"
        SelectProps={{ displayEmpty: true }}
      >
        <MenuItem value="">Both agents</MenuItem>
        <MenuItem value="discovery">{LEAD_SOURCE_LABELS.discovery}</MenuItem>
        <MenuItem value="harvester">{LEAD_SOURCE_LABELS.harvester}</MenuItem>
      </TextField>
      <TextField
        type="text"
        value={country}
        placeholder="Country (e.g. SA, NG, BR)"
        onChange={(e) => onChange({ country: e.target.value })}
      />
      <TextField
        select
        value={vertical ?? ""}
        onChange={(e) =>
          onChange({
            vertical: e.target.value
              ? (e.target.value as SalesVertical)
              : undefined,
          })
        }
        aria-label="Filter by vertical"
        SelectProps={{ displayEmpty: true }}
      >
        <MenuItem value="">All verticals</MenuItem>
        {VERTICALS.map((v) => (
          <MenuItem key={v} value={v}>
            {verticalLabel(v)}
          </MenuItem>
        ))}
      </TextField>
      {/* Sort — quick-pick presets that drive a SERVER-side ordering of the
          whole dataset (so a freshly-promoted lead can reach page 1). It stays
          in sync with the table's column headers, which sort the whole DB too:
          when a header click produced a non-preset ordering the value is "" and
          the trigger reads it back via `describeSort` ("Sort: Country (A–Z)").
          The closed trigger always reads "Sort: <label>" so it's unmistakably
          an ordering, not another filter. */}
      <TextField
        select
        value={activePreset ? activePreset.label : ""}
        onChange={(e) => {
          const preset = LEAD_SORT_PRESETS.find((p) => p.label === e.target.value);
          if (preset) onChange({ sort: preset.sort });
        }}
        aria-label="Sort leads"
        SelectProps={{
          displayEmpty: true,
          renderValue: () => `Sort: ${describeSort(sort)}`,
        }}
      >
        {LEAD_SORT_PRESETS.map((p) => (
          <MenuItem key={p.label} value={p.label}>
            {p.label}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}
